/**
 * Zod input schemas: newsletter. Split out of the one big schemas.ts (Oct 2026) so a session opens only the tool it
 * works on; nothing changed in the schemas themselves. Import as before, from '@daaily/shared' (or './schemas.js'
 * inside shared); the barrel re-exports every file in this folder. Shared primitives (EmailSchema, MonthSchema, …)
 * live in primitives.ts; cross-tool references are explicit imports between these files.
 */
import { z } from 'zod';
// ── Newsletter tool ─────────────────────────────────────────────────────────
export const NewsletterSaveConfigSchema = z.object({
    id: z.string().trim().min(1).max(60).regex(/^[a-z0-9-]+$/),
    name: z.string().trim().min(1).max(120),
    description: z.string().trim().max(400),
    active: z.boolean(),
    adminEmails: z.array(z.string().trim().toLowerCase().email()).max(20),
    sendDayOfMonth: z.number().int().min(1).max(28),
    deadlineDaysBefore: z.number().int().min(1).max(20),
    reminderDaysBefore: z.number().int().min(1).max(25),
    autoSend: z.boolean(),
    recipientMode: z.enum(['all_hub', 'custom']),
    customRecipients: z.array(z.string().trim().toLowerCase().email()).max(500),
    sectionTemplate: z.array(z.object({
        key: z.string().trim().min(1).max(60).regex(/^[a-z0-9-]+$/),
        title: z.string().trim().min(1).max(160),
        assigneeEmail: z.string().trim().toLowerCase().email().nullable(),
        autoSource: z.enum(['monthly-snapshot', 'top-content', 'top-catalog', 'social-media', 'brand-trends']).nullish(),
    })).max(30),
    headerStyle: z.enum(['classic', 'deep-blue', 'sky', 'bold-blue', 'frame']).optional(),
    cadence: z.enum(['monthly', 'quarterly']).optional(),
    footerLinkUrl: z.string().trim().url().max(500).nullish(),
    footerCta: z.object({
        headline: z.string().trim().min(1).max(160),
        subline: z.string().trim().max(160).nullish(),
        buttonLabel: z.string().trim().min(1).max(60),
        emails: z.array(z.string().trim().toLowerCase().email()).min(1).max(5),
        subject: z.string().trim().max(120).nullish(),
    }).nullish(),
    headerBarColor: z.string().regex(/^#[0-9a-fA-F]{6}$/).nullish(),
    headerBarText: z.string().trim().max(80).nullish(),
    headerBarShowPeriod: z.boolean().nullish(),
    /** Custom masthead image (data-URL/base64, ≤3 MB decoded); omitted = keep. */
    headerImageBase64: z.string().max(4_500_000).nullish(),
    removeHeaderImage: z.boolean().optional(),
    /** Custom footer image — same handling as the header. */
    footerImageBase64: z.string().max(4_500_000).nullish(),
    removeFooterImage: z.boolean().optional(),
});
export const NewsletterIssueRefSchema = z.object({
    newsletterId: z.string().trim().min(1).max(60),
    // nullish, not optional: httpsCallable serializes `undefined` fields to null.
    issueId: z.string().regex(/^\d{4}-\d{2}$/).nullish(),
});
export const NewsletterSaveSectionSchema = z.object({
    newsletterId: z.string().trim().min(1).max(60),
    issueId: z.string().regex(/^\d{4}-\d{2}$/),
    sectionKey: z.string().trim().min(1).max(60),
    heading: z.string().trim().max(200).nullable(),
    body: z.string().max(20_000).nullable(),
    /** data-URL or raw base64 of a JPEG/PNG (≤ 3 MB decoded); null = keep, 'remove' = clear. */
    imageBase64: z.string().max(4_500_000).nullish(),
    // nullish, not optional: httpsCallable serializes undefined fields to null
    // (the recall action sends no image flags — bug 4 Sep).
    removeImage: z.boolean().nullish(),
    /** Optional SECOND image — both set = rendered side by side. */
    image2Base64: z.string().max(4_500_000).nullish(),
    removeImage2: z.boolean().nullish(),
    /** Optional "For more information" URL appended to the section. */
    moreInfoUrl: z.string().trim().url().max(500).nullish(),
    /** 'save' keeps pending; 'submit' marks done; 'decline' = nothing to share;
     *  'recall' = back to pending (content kept) so it can be reworked. */
    action: z.enum(['save', 'submit', 'decline', 'recall']),
});
export const NewsletterAddSectionSchema = z.object({
    newsletterId: z.string().trim().min(1).max(60),
    issueId: z.string().regex(/^\d{4}-\d{2}$/),
    title: z.string().trim().min(1).max(160),
    assigneeEmail: z.string().trim().toLowerCase().email().nullable(),
    /** Also add to the standing template for future issues. */
    addToTemplate: z.boolean(),
});
export const NewsletterRemoveSectionSchema = z.object({
    newsletterId: z.string().trim().min(1).max(60),
    issueId: z.string().regex(/^\d{4}-\d{2}$/),
    sectionKey: z.string().trim().min(1).max(60),
    removeFromTemplate: z.boolean(),
});
/** Admin: apply the CURRENT section template to an open issue (add new
 *  sections, retitle, reassign pending ones, reorder to template order). */
export const NewsletterSyncTemplateSchema = z.object({
    newsletterId: z.string().trim().min(1).max(60),
    issueId: z.string().regex(/^\d{4}-\d{2}$/),
});
/** Admin: fill autoSource sections of an issue from the analytics warehouse. */
export const NewsletterPrefillSchema = z.object({
    newsletterId: z.string().trim().min(1).max(60),
    issueId: z.string().regex(/^\d{4}-\d{2}$/),
});
export const NewsletterSendSchema = z.object({
    newsletterId: z.string().trim().min(1).max(60),
    issueId: z.string().regex(/^\d{4}-\d{2}$/),
    /** true = send only to the caller (preview); false = the real send. */
    test: z.boolean(),
});
//# sourceMappingURL=newsletter.js.map